// safetyAuditPatchQueue.js
// IndexedDB queue management for Safety Audit form autosave and holding websocket requests for offline capabilities

import { openDB } from "idb";
import { withIdbRetry } from "./idbRetry";

const DB_NAME = "safetyAuditPatchQueue";
const STORE_NAME = "patches";

const getDb = async () => {
  return openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: "queueId",
          autoIncrement: true,
        });
        store.createIndex("safetyAuditId", "safetyAuditId", { unique: false });
      }
    },
  });
};

export const queuePatch = async (safetyAuditId, patch) => {
  return withIdbRetry(
    async () => {
      const db = await getDb();
      return db.add(STORE_NAME, {
        safetyAuditId,
        patch,
        timestamp: Date.now(),
      });
    },
    undefined,
    "safetyAuditPatchQueue.queuePatch"
  );
};

export const getQueuedPatches = async (safetyAuditId) => {
  return withIdbRetry(
    async () => {
      const db = await getDb();
      const tx = db.transaction(STORE_NAME, "readonly");
      const index = tx.store.index("safetyAuditId");
      return index.getAll(safetyAuditId);
    },
    [],
    "safetyAuditPatchQueue.getQueuedPatches"
  );
};

export const removePatchFromQueue = async (queueId) => {
  return withIdbRetry(
    async () => {
      const db = await getDb();
      await db.delete(STORE_NAME, queueId);
    },
    undefined,
    "safetyAuditPatchQueue.removePatchFromQueue"
  );
};

export const getPendingCount = async (safetyAuditId) => {
  const patches = await getQueuedPatches(safetyAuditId);
  return patches.length;
};

export const flushQueue = async (safetyAuditId, emitFn) => {
  const patches = await getQueuedPatches(safetyAuditId);
  let successCount = 0;
  for (const item of patches) {
    const result = await emitFn(safetyAuditId, item.patch);
    // Remove from queue if: success OR server rejected it (validation error)
    // Only keep in queue if it should be retried (network issues)
    if (!result.error || !result.shouldQueue) {
      await removePatchFromQueue(item.queueId);
      if (!result.error) {
        successCount++;
      }
    }
  }
  return successCount;
};

export const clearQueue = async (safetyAuditId) => {
  return withIdbRetry(
    async () => {
      const db = await getDb();
      const tx = db.transaction(STORE_NAME, "readwrite");
      const index = tx.store.index("safetyAuditId");
      const keys = await index.getAllKeys(safetyAuditId);
      for (const key of keys) {
        await tx.store.delete(key);
      }
      await tx.done;
    },
    undefined,
    "safetyAuditPatchQueue.clearQueue"
  );
};
